import React, { useState, useRef, useEffect } from 'react';
import { GoogleDocsCaretDown, GoogleDocsArrowRight, CheckmarkIcon, MoreFontsIcon } from './editorIcons';
import MoreFontsModal from './MoreFontsModal';

// All 9 font weight types matching Image 3 from user
export const ALL_FONT_WEIGHTS = [
  { label: 'Thin', weight: '100' },
  { label: 'Extra Light', weight: '200' },
  { label: 'Light', weight: '300' },
  { label: 'Normal', weight: '400' },
  { label: 'Medium', weight: '500' },
  { label: 'Semi Bold', weight: '600' },
  { label: 'Bold', weight: '700' },
  { label: 'Extra Bold', weight: '800' },
  { label: 'Black', weight: '900' },
];

// Fonts list identical to Google Docs in Images 1 & 2
const INITIAL_FONTS = [
  {
    name: 'Amatic SC',
    family: "'Amatic SC', cursive",
    hasWeights: true,
  },
  {
    name: 'Arial',
    family: 'Arial, sans-serif',
    hasWeights: true,
  },
  {
    name: 'Caveat',
    family: "'Caveat', cursive",
    hasWeights: true,
  },
  {
    name: 'Comfortaa',
    family: "'Comfortaa', cursive",
    hasWeights: true,
  },
  {
    name: 'Comic Sans MS',
    family: "'Comic Sans MS', cursive, sans-serif",
    hasWeights: false,
  },
  {
    name: 'Courier New',
    family: "'Courier New', monospace",
    hasWeights: true,
  },
  {
    name: 'EB Garamond',
    family: "'EB Garamond', serif",
    hasWeights: true,
  },
  {
    name: 'Georgia',
    family: 'Georgia, serif',
    hasWeights: true,
  },
  {
    name: 'Impact',
    family: 'Impact, Charcoal, sans-serif',
    hasWeights: false,
  },
  {
    name: 'Lexend',
    family: "'Lexend', sans-serif",
    hasWeights: true,
  },
  {
    name: 'Lobster',
    family: "'Lobster', cursive",
    hasWeights: false,
  },
  {
    name: 'Lora',
    family: "'Lora', serif",
    hasWeights: true,
  },
  {
    name: 'Merriweather',
    family: "'Merriweather', serif",
    hasWeights: true,
  },
  {
    name: 'Montserrat',
    family: "'Montserrat', sans-serif",
    hasWeights: true,
  },
  {
    name: 'Nunito',
    family: "'Nunito', sans-serif",
    hasWeights: true,
  },
  {
    name: 'Oswald',
    family: "'Oswald', sans-serif",
    hasWeights: true,
  },
  {
    name: 'Pacifico',
    family: "'Pacifico', cursive",
    hasWeights: false,
  },
  {
    name: 'Playfair Display',
    family: "'Playfair Display', serif",
    hasWeights: true,
  },
  {
    name: 'Roboto',
    family: "'Roboto', sans-serif",
    hasWeights: true,
  },
  {
    name: 'Roboto Mono',
    family: "'Roboto Mono', monospace",
    hasWeights: true,
  },
  {
    name: 'Roboto Serif',
    family: "'Roboto Serif', serif",
    hasWeights: true,
  },
  {
    name: 'Spectral',
    family: "'Spectral', serif",
    hasWeights: true,
  },
  {
    name: 'Times New Roman',
    family: "'Times New Roman', serif",
    hasWeights: true,
  },
  {
    name: 'Trebuchet MS',
    family: "'Trebuchet MS', sans-serif",
    hasWeights: true,
  },
  {
    name: 'Verdana',
    family: 'Verdana, sans-serif',
    hasWeights: true,
  },
];

const FontDropdown = ({
  value = 'Arial',
  onChange,
  onSaveSelection,
  onRestoreSelection,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubmenuFont, setActiveSubmenuFont] = useState(null);
  const [submenuTop, setSubmenuTop] = useState(0);
  const [fontsList, setFontsList] = useState(INITIAL_FONTS);
  const [showMoreModal, setShowMoreModal] = useState(false);

  const dropdownRef = useRef(null);
  const scrollListRef = useRef(null);
  const activeRowRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setActiveSubmenuFont(null);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  // Clean value for matching
  const cleanValue = (value || 'Arial').replace(/['"]/g, '').toLowerCase().trim();

  // Find currently active font
  const currentFontObj = fontsList.find((f) => {
    const cleanName = f.name.toLowerCase();
    return cleanValue.includes(cleanName) || cleanName.includes(cleanValue);
  }) || { name: value && value !== 'default' ? value.split(',')[0].replace(/['"]/g, '') : 'Arial' };

  const handleToggle = (e) => {
    e.preventDefault();
    if (!isOpen && onSaveSelection) {
      onSaveSelection();
    }
    setIsOpen((prev) => !prev);
    setActiveSubmenuFont(null);
  };

  const calculateSubmenuPosition = (rowEl) => {
    if (!rowEl || !dropdownRef.current) return 0;
    const menuEl = dropdownRef.current.querySelector('.rte-gdocs-dropdown-menu');
    if (!menuEl) return 0;

    const rowRect = rowEl.getBoundingClientRect();
    const menuRect = menuEl.getBoundingClientRect();
    let topOffset = rowRect.top - menuRect.top;

    // Boundary check so submenu does not overflow bottom of viewport
    const winHeight = window.innerHeight;
    const submenuHeight = 330; // 9 items approx height
    if (rowRect.top + submenuHeight > winHeight) {
      const overflow = (rowRect.top + submenuHeight) - winHeight;
      topOffset = Math.max(8, topOffset - overflow - 12);
    }
    return topOffset;
  };

  const handleArrowClick = (font, e) => {
    e.stopPropagation();
    e.preventDefault();
    if (activeSubmenuFont?.name === font.name) {
      setActiveSubmenuFont(null);
      activeRowRef.current = null;
    } else {
      const rowEl = e.currentTarget.closest('.rte-gdocs-font-item-row');
      activeRowRef.current = rowEl;
      const top = calculateSubmenuPosition(rowEl);
      setSubmenuTop(top);
      setActiveSubmenuFont(font);
    }
  };

  const handleRowMouseEnter = (font, e) => {
    if (font.hasWeights) {
      const rowEl = e.currentTarget;
      activeRowRef.current = rowEl;
      const top = calculateSubmenuPosition(rowEl);
      setSubmenuTop(top);
      setActiveSubmenuFont(font);
    } else {
      setActiveSubmenuFont(null);
      activeRowRef.current = null;
    }
  };

  const handleScrollList = () => {
    if (activeSubmenuFont && activeRowRef.current) {
      const top = calculateSubmenuPosition(activeRowRef.current);
      setSubmenuTop(top);
    }
  };

  const handleSelectFont = (font, weight = null, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (onRestoreSelection) {
      onRestoreSelection();
    }
    if (onChange) {
      onChange(font.family, weight, font.name);
    }
    setIsOpen(false);
    setActiveSubmenuFont(null);
    activeRowRef.current = null;
  };

  const handleAddMoreFont = (fontObj) => {
    const exists = fontsList.some((f) => f.name.toLowerCase() === fontObj.name.toLowerCase());
    if (!exists) {
      setFontsList((prev) => [
        {
          name: fontObj.name,
          family: fontObj.family,
          hasWeights: true,
        },
        ...prev,
      ]);
    }
    if (onRestoreSelection) {
      onRestoreSelection();
    }
    if (onChange) {
      onChange(fontObj.family, null, fontObj.name);
    }
    setIsOpen(false);
  };

  return (
    <div className="rte-gdocs-dropdown-container" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        className={`rte-gdocs-toolbar-btn rte-gdocs-font-trigger ${isOpen ? 'active' : ''}`}
        onMouseDown={(e) => {
          if (onSaveSelection) onSaveSelection();
        }}
        onClick={handleToggle}
        title="Font family"
      >
        <span
          className="rte-gdocs-font-btn-text"
          style={{ fontFamily: currentFontObj.family || 'Arial, sans-serif' }}
        >
          {currentFontObj.name}
        </span>
        <span className="rte-gdocs-arrow-icon">
          <GoogleDocsCaretDown />
        </span>
      </button>

      {/* Main Dropdown Menu */}
      {isOpen && (
        <div
          className="rte-gdocs-dropdown-menu rte-gdocs-font-menu"
          onMouseDown={(e) => e.preventDefault()}
        >
          {/* Top Item: More fonts */}
          <div
            className="rte-gdocs-font-item-row rte-gdocs-more-fonts-row"
            onClick={(e) => {
              e.stopPropagation();
              setShowMoreModal(true);
              setIsOpen(false);
              setActiveSubmenuFont(null);
            }}
          >
            <div className="rte-gdocs-item-check-col">
              <MoreFontsIcon className="rte-gdocs-more-icon" />
            </div>
            <div className="rte-gdocs-item-label-col rte-gdocs-more-fonts-label">
              More fonts
            </div>
            <div className="rte-gdocs-item-arrow-col" />
          </div>

          {/* Divider */}
          <div className="rte-gdocs-menu-divider" />

          {/* Scrollable Font List */}
          <div
            className="rte-gdocs-font-list-scroll"
            ref={scrollListRef}
            onScroll={handleScrollList}
          >
            {fontsList.map((font) => {
              const cleanFontName = font.name.toLowerCase();
              const isSelected =
                cleanValue.includes(cleanFontName) ||
                cleanFontName.includes(cleanValue);
              const isArrowActive = activeSubmenuFont?.name === font.name;

              return (
                <div
                  key={font.name}
                  className={`rte-gdocs-font-item-row ${isSelected ? 'selected' : ''} ${isArrowActive ? 'hover-active' : ''}`}
                  onMouseEnter={(e) => handleRowMouseEnter(font, e)}
                  onClick={(e) => handleSelectFont(font, null, e)}
                >
                  {/* Left checkmark column */}
                  <div className="rte-gdocs-item-check-col">
                    {isSelected && <CheckmarkIcon className="rte-gdocs-check-icon" />}
                  </div>

                  {/* Font Name rendered in actual font */}
                  <div
                    className="rte-gdocs-item-label-col"
                    style={{ fontFamily: font.family }}
                  >
                    {font.name}
                  </div>

                  {/* Small, delicate right arrow for weights */}
                  <div
                    className={`rte-gdocs-item-arrow-col ${isArrowActive ? 'arrow-active' : ''}`}
                    onClick={(e) => handleArrowClick(font, e)}
                    title={font.hasWeights ? `Show ${font.name} weights` : undefined}
                  >
                    {font.hasWeights && <GoogleDocsArrowRight />}
                  </div>
                </div>
              );
            })}
          </div>

          {/* SUBMENU BOX RENDERED OUTSIDE THE SCROLL CONTAINER (Direct child of dropdown menu) */}
          {activeSubmenuFont && (
            <div
              className="rte-gdocs-flyout-submenu rte-gdocs-weights-submenu"
              style={{
                top: `${submenuTop}px`,
                left: 'calc(100% + 2px)',
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              {ALL_FONT_WEIGHTS.map((w) => (
                <div
                  key={w.weight}
                  className="rte-gdocs-flyout-item"
                  style={{
                    fontFamily: activeSubmenuFont.family,
                    fontWeight: w.weight,
                  }}
                  onClick={(e) => handleSelectFont(activeSubmenuFont, w.weight, e)}
                >
                  {w.label}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* More Fonts Modal */}
      <MoreFontsModal
        isOpen={showMoreModal}
        onClose={() => setShowMoreModal(false)}
        onSelectFont={handleAddMoreFont}
        activeFontName={currentFontObj.name}
      />
    </div>
  );
};

export default FontDropdown;
